<template>
    <CSidebar
        color-scheme="light"
        position="fixed"
        :visible="visible"
        :unfoldable="minimized"
    >
        <CSidebarBrand
            class="d-md-down-none"
        >
            <a
                :href="dashboardUrl"
            >
                <img
                    :alt="tenantSettings.company_name"
                    :src="tenantLogo"
                    class="c-sidebar-brand-full py-1"
                >
            </a>
        </CSidebarBrand>

        <DSidebarSearchbox />

        <CSidebarNav>
            <div
                v-for="(sidebarItem) in sidebarItems"
                :key="sidebarItem.route"
            >
                <CNavItem
                    v-if="sidebarItem.isStandaloneItem && hasPermission(sidebarItem.permissions)"
                >
                    <Link
                        :id="sidebarItem.id"
                        :href="$route().has(sidebarItem.children[0].route) ? $route(sidebarItem.children[0].route) : ''"
                        :class="currentRouteInfo.activeRoutes.includes(sidebarItem.children[0].route) ? 'activeItem' : ''"
                        class="nav-link"
                    >
                        <DIcon
                            v-if="sidebarItem.icon"
                            :name="sidebarItem.icon"
                            class="c-sidebar-nav-icon"
                        />
                        <span class="sidebar-item-label">{{ __(sidebarItem.label, sidebarTranslationParameters) }}</span>
                    </Link>
                </CNavItem>
                <CNavGroup
                    v-if="!sidebarItem.isStandaloneItem && hasPermission(sidebarItem.permissions)"
                    :add-toggler-id="sidebarItem.id"
                    :visible="currentRouteInfo.expandDropdowns.includes(sidebarItem.sidebarItemName)"
                    @visible-change="setSidebarIds"
                >
                    <template #togglerContent>
                        <DIcon
                            v-if="sidebarItem.icon"
                            :name="sidebarItem.icon"
                            class="c-sidebar-nav-icon"
                        />
                        <span class="sidebar-item-label">{{ __(sidebarItem.label, sidebarTranslationParameters) }}</span>
                    </template>
                    <div
                        v-for="(sidebarChildItem, key) in getSortedBySecondDropdown(sidebarItem.children)"
                        :key="key"
                        class="mb-0"
                    >
                        <CNavItem
                            v-if="sidebarChildItem.showInSidebar && !sidebarChildItem.showSecondDropdown && hasPermission(sidebarChildItem.permissions)"
                        >
                            <Link
                                :id="sidebarChildItem.id"
                                :href="$route().has(sidebarChildItem.route) ? $route(sidebarChildItem.route) : ''"
                                :class="currentRouteInfo.activeRoutes.includes(sidebarChildItem.route) ? 'activeItem' : ''"
                                class="nav-link"
                            >
                                <span class="sidebar-item-label">{{ __(sidebarChildItem.label, sidebarTranslationParameters) }}</span>
                            </Link>
                        </CNavItem>
                        <CNavGroup
                            v-if="sidebarChildItem.showInSidebar && sidebarChildItem.showSecondDropdown && hasPermission(sidebarChildItem.permissions)"
                            :add-toggler-id="sidebarChildItem.id"
                            :visible="currentRouteInfo.expandDropdowns.includes(sidebarChildItem.sidebarItemName)"
                            @visible-change="setSidebarIds"
                        >
                            <template #togglerContent>
                                <DIcon
                                    v-if="sidebarChildItem.icon"
                                    :name="sidebarChildItem.icon"
                                    class="c-sidebar-nav-icon"
                                />
                                <span class="sidebar-item-label">{{ __(sidebarChildItem.label, sidebarTranslationParameters) }}</span>
                            </template>
                            <div
                                v-for="(sidebarChildChildItem, key2) in sidebarChildItem.children"
                                :key="key2"
                            >
                                <CNavItem
                                    v-if="sidebarChildChildItem.showInSidebar && hasPermission(sidebarChildChildItem.permissions)"
                                >
                                    <Link
                                        :id="sidebarChildChildItem.id"
                                        style="padding-left: 68px"
                                        :href="$route().has(sidebarChildChildItem.route) ? $route(sidebarChildChildItem.route) : ''"
                                        :class="currentRouteInfo.activeRoutes.includes(sidebarChildChildItem.route) ? 'activeItem' : ''"
                                        class="nav-link"
                                    >
                                        <span class="sidebar-item-label">{{ __(sidebarChildChildItem.label, sidebarTranslationParameters) }}</span>
                                    </Link>
                                </CNavItem>
                            </div>
                        </CNavGroup>
                    </div>
                </CNavGroup>
            </div>
            <DSidebarVersionInfo />
        </CSidebarNav>
        <div class="sidebar-footer">
            <span
                v-if="environmentLabel"
                class="env-indicator"
            >{{ environmentLabel }}</span>
            <CSidebarToggler
                class="d-md-down-none"
                @click="toggleMinimizer"
            />
        </div>
    </CSidebar>
</template>

<script>
import DSidebarVersionInfo from '~@/Components/App/Sidebar/DSidebarVersionInfo'
import DSidebarSearchbox from '~@/Components/App/Sidebar/DSidebarSearchbox'
import sitemap from '~@/Configurations/sitemap/sitemap'
import { getActiveSidebarItem } from '~@/Configurations/sitemap'
import { CNavGroup, CNavItem, CSidebar, CSidebarBrand, CSidebarNav, CSidebarToggler } from '@coreui/vue-pro'
import { router } from '@inertiajs/vue3'

export default {
    name: 'Sidebar',
    components: {
        DSidebarSearchbox,
        DSidebarVersionInfo,
        CSidebar,
        CSidebarBrand,
        CSidebarNav,
        CNavItem,
        CNavGroup,
        CSidebarToggler
    },
    props: {
        minimized: {
            type: Boolean,
            default: false
        }
    },
    emits: ['sidebar-toggle'],
    data () {
        return {
            visible: window.innerWidth >= 765,
            sidebarItems: [],
            currentRouteInfo: {
                activeRoutes: [],
                expandDropdowns: []
            },
            isDarkMode: false
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        dashboardUrl () {
            return this.$route('dashboard.index', {}, true)
        },
        tenantLogo () {
            // Use dark mode logo if available and in dark mode
            if (this.isDarkMode && this.tenantSettings.logo_dark) {
                return `data:image/svg+xml;base64,${this.tenantSettings.logo_dark}`
            }

            // Fall back to regular logo
            if (this.tenantSettings.logo === '') {
                return '/img/logo.dogado.svg'
            }

            return `data:image/svg+xml;base64,${this.tenantSettings.logo}`
        },
        tenantSettings () {
            return this.$page.props.tenantSettings
        },
        sidebarTranslationParameters () {
            return this.$page.props.sidebarTranslationParameters
        },
        environmentLabel () {
            if (this.$page.props.isProductionEnvironment) return null
            const env = this.$page.props.environment
            return env ? env.toUpperCase() : null
        }
    },
    beforeMount () {
        this.loadSidebar()
    },
    mounted () {
        // Initialize theme state from localStorage
        this.isDarkMode = localStorage.getItem('theme') === 'dark'

        // Listen for theme changes
        this.emitter.on('theme-changed', (isDark) => {
            this.isDarkMode = isDark
        })

        router.on('navigate', () => {
            this.currentRouteInfo.activeRoutes = []
            this.currentRouteInfo.expandDropdowns = []
            this.getCurrentRouteInfo()
        })

        window.addEventListener('resize', () => {
            this.visible = window.innerWidth >= 765
        })

        this.setSidebarIds()
    },
    methods: {
        getCurrentRouteInfo () {
            this.currentRouteInfo = getActiveSidebarItem(sitemap(this.$page), this.$route().current(), this.$page.props)
        },
        toggleMinimizer () {
            this.emitter.emit('sidebar-toggle')
        },
        loadSidebar () {
            this.sidebarItems = sitemap(this.$page)
            this.getCurrentRouteInfo()
        },
        hasPermission (permissions) {
            if (permissions === undefined) return true
            return permissions.some(item => this.userPermissions.includes(item))
        },
        getSortedBySecondDropdown (children) {
            return children.sort((child1, child2) => {
                if (child1.showSecondDropdown && !child2.showSecondDropdown) {
                    return 1
                }

                if (!child1.showSecondDropdown && child2.showSecondDropdown) {
                    return -1
                }

                return 0
            })
        },
        setSidebarIds () {
            const navGroups = document.getElementsByClassName('nav-group')

            for (const navGroup of navGroups) {
                const aToggle = navGroup.querySelectorAll('a.nav-group-toggle')
                if (!aToggle[0]) {
                    continue
                }

                aToggle[0].id = navGroup.getAttribute('add-toggler-id')
            }
        }
    }
}
</script>

<style lang="scss">
.sidebar-footer {
    display: flex;
    align-items: center;
    flex-shrink: 0;

    .env-indicator {
        flex: 1;
        text-align: center;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.1em;
        color: #fff;
        background: #e53e3e;
        padding: 3px 8px;
        border-radius: 3px;
        margin: 6px 8px;
    }
}

.sidebar-narrow-unfoldable .sidebar-footer .env-indicator {
    display: none;
}

.padding {
    padding-left: 15px
}
.ps {
    height: 100%;
}
.sidebar-nav .nav-group.show > .nav-group-toggle::after {
    transform: rotate(-90deg);
}
.sidebar-nav .nav-group .nav-group-toggle {
    &::after {
        width: 10px;
        height: 10px;
        background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3C!DOCTYPE svg PUBLIC '-//W3C//DTD SVG 1.1//EN' 'http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd'%3E%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' version='1.1' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath fill='rgba(0,0,0,0.2)' d='M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z' /%3E%3C/svg%3E");
    }

    &:hover {
        background: var(--colorPrimary) !important;
        color: var(--colorPrimaryText) !important;

        &::after {
            background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3C!DOCTYPE svg PUBLIC '-//W3C//DTD SVG 1.1//EN' 'http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd'%3E%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' version='1.1' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath fill='rgba(0,0,0,0.2)' d='M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z' /%3E%3C/svg%3E");
        }
        & .c-sidebar-nav-icon {
            color: var(--colorPrimaryText) !important;
        }
    }

}
.sidebar {
    & .nav-link:hover {
        background: var(--colorPrimary) !important;
    }

    & .sidebar-brand {
        background: #fff !important;
        padding: 25px 0px;

        & img {
            height: 40px;
        }
    }

    & .nav-link {
        color: rgba(0, 0, 21, .8) !important;
    }

    & .nav-group.show {
        background: #f2f2f2;

        & .nav-link {
            background: #f2f2f2;
        }
    }

    & .nav-item {
        & .nav-link {
            &:hover {
                background: var(--colorPrimary) !important;
                color: var(--colorPrimaryText) !important;

                & .c-sidebar-nav-icon {
                    color: var(--colorPrimaryText) !important;
                }
            }
        }
    }

    & .c-sidebar-nav-icon {
        margin-left: -1rem;
        flex: 0 0 56px;
        color: rgba(0,0,21,.5);
    }

    & .sidebar-nav .nav-group-items .nav-link {
        padding-left: 56px;
    }
}
.sidebar-narrow-unfoldable:not(:hover) {
    overflow-x: hidden;

    .sidebar-item-label {
        color: transparent;
    }

    .sidebar-brand {
        visibility: hidden;
    }

    .d-sidebar-searchbox {
        padding: 10px;
        margin-left: 10px;

        & > span:nth-child(n+2) {
            color: transparent;
        }
    }
}

.activeItem {
    background: var(--colorPrimary) !important;
    color: var(--colorPrimaryText) !important;

    &::after {
        background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3C!DOCTYPE svg PUBLIC '-//W3C//DTD SVG 1.1//EN' 'http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd'%3E%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' version='1.1' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath fill='rgba(0,0,0,0.2)' d='M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z' /%3E%3C/svg%3E");
    }
    & .c-sidebar-nav-icon {
        color: var(--colorPrimaryText) !important;
    }
}
</style>
